import React, { useEffect, useMemo, useRef, useState } from 'react'

import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts'
import { motion } from 'framer-motion'
import Confetti from 'react-confetti'

/**
 * Payload esperado (ejemplo):
 * {
 *   data: [
 *     { QuestionId: 7, CalculatedPointsByZone: { canels: 1, tecate: 1, tequis: 1 } },
 *     { QuestionId: 6, CalculatedPointsByZone: { canels: 1, tequis: 1 } },
 *     ...
 *   ],
 *   idTile: 3.1,
 *   isActiveWomensMode: false,
 *   type: "finalResults"
 * }
 */

/* =========================
 * 1) Constantes y mapeos
 * ========================= */

const MODES = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
}

// Definimos zonas con un slug (clave que llega en payload), etiqueta visible y modo.
const ZONES = [
  // Varones
  { slug: 'laboratorio tequis', label: 'Laboratorio Tequis', internal: 'Zona Laboratorio Tequis', mode: MODES.MALE },
  // { slug: 'canels', label: 'Canels', internal: 'Zona Canels', mode: MODES.MALE },
  { slug: 'san luis', label: 'San Luis', internal: 'Zona San Luis', mode: MODES.MALE },
  { slug: 'seguros el potosí', label: 'Seguros El Potosí', internal: 'Zona Seguros El Potosí', mode: MODES.MALE },
  { slug: 'red cola', label: 'Red Cola', internal: 'Zona Red Cola', mode: MODES.MALE },
  // { slug: 'tecate', label: 'Tecate', internal: 'Zona Tecate', mode: MODES.MALE },
  { slug: 'corona', label: 'Corona', internal: 'Zona Corona', mode: MODES.MALE },
  { slug: 'moctezuma', label: 'Moctezuma', internal: 'Zona Moctezuma', mode: MODES.MALE },
  // { slug: 'nissan', label: 'Nissan', internal: 'Zona Nissan', mode: MODES.MALE },
  { slug: 'atleti', label: 'Atleti', internal: 'Zona Atleti', mode: MODES.MALE },
  { slug: 'mobil', label: 'Mobil', internal: 'Zona Mobil', mode: MODES.MALE },
  // { slug: 'lounge ron potosí', label: 'Lounge Ron Potosí', internal: 'Zona Lounge Ron Potosí', mode: MODES.MALE },
  { slug: 'palcos', label: 'Palcos', internal: 'Zona Palcos', mode: MODES.MALE },
  // Femenil
  { slug: 'azul', label: 'Azul', internal: 'Zona Azul', mode: MODES.FEMALE },
  { slug: 'oro', label: 'Oro', internal: 'Zona Oro', mode: MODES.FEMALE },

  // Temporales Copa Escuelas ADSL
  // { slug: 'lucho', label: 'Lucho', internal: 'Team Lucho', mode: MODES.MALE },
  // { slug: 'tunita', label: 'Tunita', internal: 'Team Tunita', mode: MODES.MALE },
  // { slug: 'lucho', label: 'Lucho', internal: 'Team Lucho', mode: MODES.FEMALE },
  // { slug: 'tunita', label: 'Tunita', internal: 'Team Tunita', mode: MODES.FEMALE },

  // Santos Agnóstico
  { slug: 'zona1', label: 'Zona Oriente', internal: 'Zona Oriente', mode: MODES.MALE },
  { slug: 'zona2', label: 'Zona Poniente', internal: 'Zona Poniente', mode: MODES.MALE },
  { slug: 'zona1', label: 'Zona Oriente', internal: 'Zona Oriente', mode: MODES.FEMALE },
  { slug: 'zona2', label: 'Zona Poniente', internal: 'Zona Poniente', mode: MODES.FEMALE },
]

// Utilidad: normaliza a slug comparable (lowercase sin tildes básicas)
const normalize = (s) =>
  (s ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

const mapToCanonicalSlug = (rawKey) => {
  const norm = normalize(rawKey).replace(/\s+/g, '')
  if (norm.includes('oriente') || norm.includes('local') || norm === 'zona1' || norm === 'noa1' || norm === 'lucho' || norm === 'teamlucho') {
    return 'zona1'
  }
  if (norm.includes('poniente') || norm.includes('visitante') || norm === 'zona2' || norm === 'noa2' || norm === 'tunita' || norm === 'teamtunita') {
    return 'zona2'
  }
  return normalize(rawKey)
}

/* =========================
 * 2) Utilidades puras
 * ========================= */

/**
 * Retorna arreglo de zonas del modo activo.
 */
function getModeZones(isActiveWomensMode) {
  const mode = isActiveWomensMode ? MODES.FEMALE : MODES.MALE
  return ZONES.filter((z) => z.mode === mode)
}

/**
 * Agrega puntos por zona (del modo activo) a partir del payload.
 * Retorna un mapa: { slug: totalPoints }
 */
function aggregatePointsByZone(payload, isActiveWomensMode) {
  const modeZones = getModeZones(isActiveWomensMode)
  const allowedSlugs = new Set(modeZones.map((z) => normalize(z.slug)))
  const result = {}
  for (const slug of allowedSlugs) result[slug] = 0

  const entries = Array.isArray(payload?.data) ? payload.data : []

  for (const item of entries) {
    const calc = item?.CalculatedPointsByZone || item?.calculatedPointsByZone

    if (!calc || typeof calc !== 'object') continue

    for (const [zoneKey, pts] of Object.entries(calc)) {
      const slug = mapToCanonicalSlug(zoneKey)
      if (allowedSlugs.has(slug)) {
        const val = Number(pts) || 0
        result[slug] = (result[slug] ?? 0) + val
      }
    }
  }
  return result
}

/**
 * Determina si NO hubo puntos calculados en ninguna pregunta del payload.
 * (i.e., todas las preguntas carecen de CalculatedPointsByZone o vienen vacías)
 */
function noCalculatedPointsAtAll(payload) {
  const entries = Array.isArray(payload?.data) ? payload.data : []
  
  if (entries.length === 0) return true

  return entries.every((item) => {
    const c = item?.CalculatedPointsByZone || item?.calculatedPointsByZone

    return !c || Object.keys(c).length === 0
  })
}

/**
 * Construye datos para la gráfica a partir del mapa { slug: points }.
 */
function buildChartData(pointsMap, isActiveWomensMode) {
  const modeZones = getModeZones(isActiveWomensMode)
  return modeZones.map((z) => ({
    slug: z.slug,
    name: z.label, // lo que verá el usuario en el eje X
    value: pointsMap[normalize(z.slug)] ?? 0,
  }))
}

/**
 * Genera datos aleatorios para animación (mismo shape que buildChartData).
 * El rango de valores se puede ajustar. Se usa un rango pequeño para “suspenso”.
 */
function randomChartData(isActiveWomensMode, max = 20) {
  const modeZones = getModeZones(isActiveWomensMode)
  return modeZones.map((z) => ({
    slug: z.slug,
    name: z.label,
    value: Math.floor(Math.random() * max) + 1,
  }))
}

// finalData: [{ slug, name, value }]
// totalZones: cantidad de zonas del modo activo (finalChartData.length)
/**
 * Calcula zonas ganadoras a partir de datos definitivos [{slug,name,value}].
 * Reglas especiales:
 *  - Si max = 0 → pick aleatoria (una sola ganadora).
 */
const computeWinners = (finalData, totalZones) => {
  if (!finalData?.length) {
    return { winners: [], allEqualPositive: false, pickedRandom: null, allZonesTied: false }
  }

  const values = finalData.map((d) => Number(d.value) || 0)
  const max = Math.max(...values)

  // Todos en 0 → elegir una al azar
  if (max === 0) {
    const randomIdx = Math.floor(Math.random() * finalData.length)
    const picked = finalData[randomIdx]
    return {
      winners: [picked],
      allEqualPositive: false,
      pickedRandom: picked,
      allZonesTied: false,
    }
  }

  const winners = finalData.filter((d) => Number(d.value) === max)

  // Empate total si ganadoras == total de zonas del modo activo y max > 0
  const allZonesTied = winners.length === totalZones && max > 0

  return { winners, allEqualPositive: allZonesTied, pickedRandom: null, allZonesTied }
}

/* =========================
 * 3) Hook de estado/flujo
 * ========================= */

function useResultsFlow(payload) {
  // Fases: 'animating' → 'reveal' → 'winner'
  const [phase, setPhase] = useState('animating')
  const [animatedData, setAnimatedData] = useState([])
  const [finalData, setFinalData] = useState([])
  const [winners, setWinners] = useState([])
  const [allEqualPositive, setAllEqualPositive] = useState(false)
  const [pickedRandom, setPickedRandom] = useState(null)
  const intervalRef = useRef(null)
  const revealTimeoutRef = useRef(null)
  const winnerTimeoutRef = useRef(null)
  const [allZonesTied, setAllZonesTied] = useState(false)

  const isActiveWomensMode = !!payload?.isActiveWomensMode

  // Cálculos estáticos derivados del payload
  const pointsMap = useMemo(() => aggregatePointsByZone(payload, isActiveWomensMode), [payload, isActiveWomensMode])
  const finalChartData = useMemo(() => buildChartData(pointsMap, isActiveWomensMode), [pointsMap, isActiveWomensMode])
  const noPointsAtAll = useMemo(() => noCalculatedPointsAtAll(payload), [payload])

  const totalZones = finalChartData.length


  // //--- Desarrollo ---//
  // useEffect(() => {
  //   // DEBUG (quítalo luego)
  //   console.log({
  //     phase,
  //     isActiveWomensMode,
  //     animatedData,
  //     finalData,
  //     winners,
  //     allZonesTied,
  //     pointsMap,
  //     noPointsAtAll
  //     //noPointsAtAll: noPointsAtAll(payload)
  //   })
  // }, [phase, animatedData, finalData, winners, allZonesTied, isActiveWomensMode])
  // //--- Desarrollo ---//
  

  useEffect(() => {
    // Si no hay zonas para el modo, salimos
    if (finalChartData.length === 0) {
      setPhase('winner')
      setAnimatedData([])
      setFinalData([])
      setWinners([])
      setAllEqualPositive(false)
      setPickedRandom(null)
      return
    }

    // 1) Empezar animación (valores aleatorios)
    setPhase('animating')
    setAnimatedData(randomChartData(isActiveWomensMode))
    intervalRef.current = setInterval(() => {
      setAnimatedData(randomChartData(isActiveWomensMode))
    }, 120)

    // 2) A los ~3s, revelar valores reales
    revealTimeoutRef.current = setTimeout(() => {
      clearInterval(intervalRef.current)
      setAnimatedData(finalChartData)
      setFinalData(finalChartData)
      setPhase('reveal')

      // 3) Breve pausa y luego calcular ganadores
      winnerTimeoutRef.current = setTimeout(() => {
        // Regla especial: si no hubo puntos calculados en ninguna pregunta → “Ganaron todas”
        if (noPointsAtAll) {
          // console.log('[Winners check]', {
          //   totalZones,
          //   finalData: finalChartData,
          //   winners,
          //   allZonesTied,
          //   noPointsAtAll
          // })

          setWinners(finalChartData) // todas ganan
          setAllEqualPositive(true)  // para el mensaje “¡Ganaron todas…!”
          setAllZonesTied(true)
          setPickedRandom(null)
          setPhase('winner')
          return
        }

        // const { winners, allEqualPositive, pickedRandom, allZonesTied } = computeWinners(finalChartData)
        // setWinners(winners)
        // setAllEqualPositive(allEqualPositive)
        // setPickedRandom(pickedRandom)
        // setAllZonesTied(allZonesTied)
        // setPhase('winner')

        const { winners, allEqualPositive, pickedRandom, allZonesTied } = computeWinners(finalChartData, totalZones)

        //// DEBUG — imprime SIEMPRE el resultado final del cómputo
        // console.log('[Winners check FINAL]', {
        //   totalZones,
        //   finalData: finalChartData,
        //   winners,
        //   allZonesTied
        // })

        setWinners(winners)
        setAllEqualPositive(allEqualPositive)
        setPickedRandom(pickedRandom)
        setAllZonesTied(allZonesTied)
        setPhase('winner')
      }, 500)
    }, 3000)

    // Limpieza al desmontar o al cambiar el payload
    return () => {
      clearInterval(intervalRef.current)
      clearTimeout(revealTimeoutRef.current)
      clearTimeout(winnerTimeoutRef.current)
    }
  }, [payload, isActiveWomensMode, finalChartData, noPointsAtAll])

  return { phase, animatedData, finalData, winners, allEqualPositive, pickedRandom, isActiveWomensMode, allZonesTied }
}

/* =========================
 * 4) Componente principal
 * ========================= */

const Results = ({ payload }) => {
  //const { phase, animatedData, finalData, winners, allEqualPositive, pickedRandom, isActiveWomensMode, allZonesTied } = useResultsFlow(payload)
  //const { phase, animatedData, winners, pickedRandom, allZonesTied } = useResultsFlow(payload)
  const { phase, animatedData, finalData, winners, pickedRandom, allZonesTied, isActiveWomensMode } = useResultsFlow(payload)



  // //--- Desarrollo ---//
  // useEffect(() => {
  //   // DEBUG — quítalo cuando termines
  //   console.log('[Results Component]', {
  //     phase,
  //     animatedData,
  //     finalData,
  //     winners,
  //     pickedRandom,
  //     allZonesTied,
  //     isActiveWomensMode
  //   })
  // }, [phase, animatedData, finalData, winners, pickedRandom, allZonesTied, isActiveWomensMode])
  // //--- Desarrollo ---//




  const showChart = phase === 'animating' || phase === 'reveal'
  const showWinner = phase === 'winner' && winners.length > 0

  // Tamaño seguro para Confetti
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024
  const height = typeof window !== 'undefined' ? window.innerHeight : 768


  useEffect(() => {
    if (showChart) {
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
    }
  }, [showChart])





  // Paletas por modo (elige tonos buenos en negro)
  const PALETTE_MALE = {
    'laboratorio tequis': '#4CC9F0',
    canels: '#F72585',
    'seguros el potosí': '#B5179E',
    'red cola': '#F94144',
    tecate: '#F3722C',
    moctezuma: '#90BE6D',
    nissan: '#F9C74F',
    mobil: '#43AA8B',
    'lounge ron potosí': '#7f43aaff',
    // Temporales Escuelas ADSL
    lucho: '#F94144',
    tunita: '#60A5FA',
    // Santos Agnóstico
    zona1: '#60A5FA',
    zona2: '#F59E0B'
  }

  const PALETTE_FEMALE = {
    azul: '#60A5FA', // azul
    oro: '#F59E0B',   // oro
    // Temporales Escuelas ADSL
    lucho: '#60A5FA', // azul
    tunita: '#F59E0B',  // oro
    // Santos Agnóstico
    zona1: '#60A5FA',
    zona2: '#F59E0B'
  }

  function getZoneColor(slug, isActiveWomensMode) {
    const key = normalize(slug)
    const palette = isActiveWomensMode ? PALETTE_FEMALE : PALETTE_MALE
    return palette[key] || '#8B5CF6' // fallback
  }





  return (
    <div className="Results__FullScreenOverlay">
      {/* 📊 Gráfica (animación → revelado) */}
      {/* {showChart && animatedData.length > 0 && (
        <div style={{ minWidth: 0, minHeight: 0 /* altura controlada por aspect / }}>
          <ResponsiveContainer width="100%" height="100%" aspect={2}>
            <BarChart data={animatedData}>
              <CartesianGrid stroke="#3a3a3a" /* grid tenue / strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={{ fill: '#eaeaea' }}
                axisLine={{ stroke: '#888' }}
                tickLine={{ stroke: '#888' }}
              />
              <YAxis
                tick={{ fill: '#eaeaea' }}
                axisLine={{ stroke: '#888' }}
                tickLine={{ stroke: '#888' }}
              />
              <Tooltip
                contentStyle={{ background: '#1f1f1f', border: '1px solid #444', color: '#fff' }}
                labelStyle={{ color: '#bbb' }}
                itemStyle={{ color: '#fff' }}
                formatter={(value) => [`${value} pts`, 'Zona']}
              />
              {/* Una barra con color por zona /}
              <Bar
                dataKey="value"
                isAnimationActive={true}
                animationDuration={400}
                // color dinámica por zona:
                fillOpacity={0.9}
              >
                {
                  // Si quieres colorear cada barra según el slug:
                  animatedData.map((entry, idx) => (
                    <cell
                      // en JSX real: usa <Cell />, aquí el tag es con mayúscula
                      /* eslint-disable jsx-a11y/aria-role /
                      key={`cell-${idx}`}
                      fill={getZoneColor(entry.slug, isActiveWomensMode)}
                    />
                  ))
                }
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )} */}

      {/* 📊 Gráfica (animación → revelado) */}
      {showChart && animatedData.length > 0 && (
        // <div className="w-full h-96">
        <div style={{ minWidth: 0, minHeight: 0, height: '24rem' }}>
          <ResponsiveContainer width="100%" height="100%" aspect={4}>
            <BarChart data={animatedData}>
              <CartesianGrid stroke="#3a3a3a" /* grid tenue */ strokeDasharray="3 3" />
              {/* <XAxis dataKey="name" tick={{ fill: '#eaeaea' }} axisLine={{ stroke: '#888' }} tickLine={{ stroke: '#888' }} /> */}
              {/* <YAxis tick={{ fill: '#eaeaea' }} axisLine={{ stroke: '#888' }} tickLine={{ stroke: '#888' }} /> */}
              {/* <Tooltip contentStyle={{ background: '#1f1f1f', border: '1px solid #444', color: '#fff' }} labelStyle={{ color: '#bbb' }} itemStyle={{ color: '#fff' }} formatter={(value) => [`${value} pts`, 'Zona']} /> */}
              {/* <Bar dataKey="value" fill="#60A5FA" /> */}
              <Bar dataKey="value" isAnimationActive animationDuration={400} fillOpacity={0.95}>
                {animatedData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={getZoneColor(entry.slug, isActiveWomensMode)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 🏆 Resultado */}
      {showWinner && (
        <>
          <Confetti width={width} height={height} recycle={false} numberOfPieces={300} />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1.12 }}
            transition={{ duration: 0.6, type: 'spring' }}
            className="text-4xl font-bold text-center mt-6"
          >
            <p><i className="bi bi-trophy fs-1"></i></p>

            {/* Mensajería de ganadores */}
            {(() => {
              if (allZonesTied) {
                {/* ¡Han empatado todas las zonas! */}
                return <p className="Results__FullScreenOverlay__LargeWinner">¡Ha habido un empate total!</p>
              }

              // Cuando ninguno respondió, se elegía uno al azar, pero lo cambié para que muestre un empate
              if (pickedRandom) {
                // return (
                //   <p className="Results__FullScreenOverlay__Title">
                //     Nadie anotó… elegimos al azar y la zona ganadora es:<br />
                //     Zona {pickedRandom.name}
                //   </p>
                // )

                {/* ¡Han empatado todas las zonas! */}
                return <p className="Results__FullScreenOverlay__LargeWinner">¡Ha habido un empate total!</p>
              }

              if (winners) {
                if (winners.length === 0) {
                  return (
                    <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate total!</p>
                  )
                }
  
                if (winners.length === 1) {
                  return (
                    <p className="Results__FullScreenOverlay__OneWinner">
                      La zona ganadora es…<br />
                      {/* Zona {winners[0].name} */}
                      {/* Temporales Escuelas ADSL */}
                      Team {winners[0].name}
                    </p>
                  )
                }
  
                return (
                  <>
                    <p className="Results__FullScreenOverlay__MultipleWinner">¡Empate! entre las zonas:</p>
                    {winners.map((w) => (
                      // <p key={w.slug} className="Results__FullScreenOverlay__MultipleWinner">Zona {w.name}</p>
                      // Temporales Escuelas ADSL
                      <p key={w.slug} className="Results__FullScreenOverlay__MultipleWinner">Team {w.name}</p>
                    ))}
                  </>
                )
              } else {
                return (
                  <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate total!</p>
                )
              }
            })()}
          </motion.div>
        </>
      )}
    </div>
  )
}

export { Results };